// Stems → the Snapshot a seed carries. Pure.
//
// The same shape as an Audiotool capture (shared/audiotool/seed-plan.ts) and
// a Live stem push (extension/src/audiotool/arrangement-push.ts): one audio
// track per file, each holding one unwarped clip from beat 0 that owns its
// own audio. That is what makes the seed page, the browser editor (which only
// hydrates clips with an `audioKey` — shared/sync/hydrate.ts), Send to Live
// and Open in Audiotool all take it with no special case.
//
// Unwarped is deliberate: the files already play in time with each other, at
// their own speed, whatever tempo is typed. The BPM only lays the grid over
// them and sizes the clips in beats.

import type { AudioClipInfo, AudioTrackInfo, Snapshot, UploadSource } from "@shared/seed-history.js";
import { NO_CLIP_COLOUR } from "@shared/clip-colour.js";
import { CENTER_PAN, UNITY_VOLUME } from "@shared/mixer.js";

export interface StemTrack {
  name: string;
  durationSec: number;
  /** The file as handed over: recorded on the clip, which stores it as WAV. */
  source: UploadSource;
}

/** No colour of its own, as an Audiotool capture has none (clip-colour.ts). */
const NO_COLOUR = NO_CLIP_COLOUR;

/** Seconds at a tempo, in beats — rounded as a Live push rounds them. */
export function beatsOf(seconds: number, bpm: number): number {
  return Math.round(((seconds * bpm) / 60) * 1e4) / 1e4;
}

export function stemsSnapshot(tracks: StemTrack[], bpm: number): Snapshot {
  const audioTracks: AudioTrackInfo[] = tracks.map((t) => ({
    name: t.name,
    mute: false,
    solo: false,
    arm: false,
    mutedViaSolo: false,
    volume: UNITY_VOLUME,
    panning: CENTER_PAN,
    groupTrackName: null,
    arrangementClips: [stemClip(t.name, beatsOf(t.durationSec, bpm), t.source)],
    takeLanes: [],
  }));
  return {
    song: {
      tempo: bpm,
      gridQuantization: { value: 0, label: "", isTriplet: false },
      // Key is optional on the form and never guessed: "unknown", not C major.
      scale: { rootNote: 0, rootNoteName: "C", name: "", mode: false, intervals: [] },
      cuePoints: [],
    },
    audioTracks,
    totals: {
      audioTrackCount: audioTracks.length,
      arrangementClipCount: audioTracks.length,
      takeLaneClipCount: 0,
    },
  };
}

function stemClip(name: string, beats: number, source: UploadSource): AudioClipInfo {
  return {
    name,
    startTime: 0,
    endTime: beats,
    duration: beats,
    startMarker: 0,
    endMarker: beats,
    looping: false,
    loopStart: 0,
    loopEnd: beats,
    color: NO_COLOUR,
    colorHex: `#${NO_COLOUR.toString(16).padStart(6, "0")}`,
    muted: false,
    // The producer's own path means nothing to anyone else.
    filePath: "",
    warping: false,
    warpMode: { value: 0, name: "Beats" },
    warpMarkerCount: 0,
    // Stamped by the server at /complete.
    audioKey: null,
    uploadSource: { format: source.format, bitDepth: source.bitDepth },
  };
}
